import { Ref } from 'vue';
import { GridApi, ColumnState } from '@ag-grid-community/core';
import { Toast } from '@agyletime/vue-3-common/src/components/Toast';

/**
 * Helper: Get visible and hidden columns from grid
 */
function getColumnVisibility(gridApi: GridApi): {
    visibleColumns: string[];
    hiddenColumnsCount: number;
} {
    const columnState = gridApi.getColumnState();
    const visibleColumns = columnState
        ?.filter((col: ColumnState) => !col.hide)
        ?.map((col: ColumnState) => col.colId) || [];
    const hiddenColumns = columnState?.filter((col: ColumnState) => col.hide) || [];

    return {
        visibleColumns,
        hiddenColumnsCount: hiddenColumns.length
    };
}

/**
 * Helper: Validate export preconditions
 * Returns true if validation passes, false otherwise
 */
function validateExport(
    gridApi: Ref<GridApi | null>,
    totalRecords: number,
    isExporting: Ref<boolean>
): boolean {
    // Prevent concurrent exports
    if (isExporting.value) {
        return false;
    }

    // Validate grid is ready
    if (!gridApi.value) {
        Toast.addToast('exportError', 'Grid not ready for export', 'ERROR');
        return false;
    }

    // Check for empty data
    if (totalRecords === 0) {
        Toast.addToast('exportNoData', 'No data available to export', 'INFO');
        return false;
    }

    return true;
}

/**
 * Helper: Validate column visibility
 * Returns true if there are visible columns, false otherwise
 */
function validateColumns(visibleColumns: string[], hiddenColumnsCount: number): boolean {
    if (visibleColumns.length === 0) {
        Toast.addToast('exportError', 'Please select at least one column to export', 'ERROR');
        return false;
    }

    // Notify about hidden columns
    if (hiddenColumnsCount > 0) {
        Toast.addToast(
            'hiddenColumnsInfo',
            `Note: ${hiddenColumnsCount} hidden column(s) will not be exported`,
            'INFO'
        );
    }

    return true;
}

function getTeamNameFromFacets(facets: Record<string, Record<string, unknown>>): string {
    const teamValue = facets?.teams?.value;
    if (!teamValue || typeof teamValue !== 'string') return 'Schedule_Team_';

    const teamIds = teamValue.split(',');
    if (teamIds.length !== 1) return 'Schedule_Team_';

    const teamId = Number(teamIds[0]);
    const userData = JSON.parse(sessionStorage.getItem('usersData') ?? '{}');
    const teamsData = userData['teamSchedule']?.teams;
    if (!teamsData) return 'Schedule_Team_';

    // teamsData is a Record<string, DropdownOption[]> grouped by parentGroupName,
    // so we must flatten all groups into a single array before searching.
    const allTeams = (Object.values(teamsData) as Array<{ id: number; label: string }[]>).flat();
    const teamName = allTeams.find((t) => t.id === teamId);
    return 'Schedule_' + (teamName?.label ?? 'Team') + '_';
}

/**
 * Helper: Perform the actual CSV export
 */
function performExport(
    gridApi: GridApi,
    visibleColumns: string[],
    config: {
        filePrefix: string;
        successMessage: string;
        errorMessage: string;
        facets: Record<string, Record<string, unknown>>;
    }
): void {
    try {
        const teamName = getTeamNameFromFacets(config.facets);
        const dateFacetValue = config.facets?.dateFacet?.value;
        const timeStamp = Array.isArray(dateFacetValue) && dateFacetValue.length >= 2
            ? `${new Date(dateFacetValue[0]).toISOString().split('T')[0]}_to_${new Date(dateFacetValue[1]).toISOString().split('T')[0]}`
            : new Date().toISOString().split('T')[0];
        const filename = `${teamName}${timeStamp}`;

        gridApi.exportDataAsCsv({
            columnKeys: visibleColumns,
            fileName: filename,
            skipColumnHeaders: false,
            skipColumnGroupHeaders: true,
            processCellCallback: (params) => {
                // Handle metadata objects (ShiftMetadata) - extract displayText
                if (params.value && typeof params.value === 'object' && 'displayText' in params.value) {
                    return params.value.displayText;
                }
                return params.value;
            },
            processHeaderCallback: (params) => {
                return params.column.getColDef().headerName || params.column.getColId();
            }
        });

        Toast.addToast('exportSuccess', config.successMessage, 'SUCCESS');
    } catch {
        Toast.addToast('exportError', config.errorMessage, 'ERROR');
    }
}

/**
 * Export AG Grid data to CSV
 * Handles validation, error handling, and user notifications
 *
 * @param gridApi - AG Grid API instance
 * @param totalRecords - Total number of records in the grid
 * @param isExporting - Reactive ref to track export state
 * @param config - Optional configuration for filename, messages and facets
 */
export const exportGridToCSV = (
    gridApi: Ref<GridApi | null>,
    totalRecords: number,
    isExporting: Ref<boolean>,
    config?: {
        filePrefix?: string;
        successMessage?: string;
        errorMessage?: string;
        facets?: Record<string, Record<string, unknown>>;
    }
): void => {
    const {
        filePrefix = 'export',
        successMessage = 'Data exported successfully!',
        errorMessage = 'Failed to export CSV. Please try again.',
        facets
    } = config || {};

    // Validate export preconditions
    if (!validateExport(gridApi, totalRecords, isExporting)) {
        return;
    }

    // Get column visibility info
    const { visibleColumns, hiddenColumnsCount } = getColumnVisibility(gridApi.value!);

    // Validate columns
    if (!validateColumns(visibleColumns, hiddenColumnsCount)) {
        return;
    }

    // Set exporting state
    isExporting.value = true;

    // Perform export
    performExport(gridApi.value!, visibleColumns, {
        filePrefix,
        successMessage,
        errorMessage,
        facets: facets ?? {}
    });

    // Reset exporting state
    setTimeout(() => {
        isExporting.value = false;
    }, 1000);
};

/**
 * Validate if export is possible
 *
 * @param gridApi - AG Grid API instance
 * @param totalRecords - Total number of records
 * @returns Object with isValid flag and error message
 */
export const validateExportReady = (
    gridApi: Ref<GridApi | null>,
    totalRecords: number
): { isValid: boolean; message?: string } => {
    if (!gridApi.value) {
        return { isValid: false, message: 'Grid not ready' };
    }

    if (totalRecords === 0) {
        return { isValid: false, message: 'No data available' };
    }

    const columnState = gridApi.value.getColumnState();
    const visibleColumns = columnState?.filter((col: ColumnState) => !col.hide) || [];

    if (visibleColumns.length === 0) {
        return { isValid: false, message: 'No visible columns' };
    }

    return { isValid: true };
};
